import { useState } from "react";
import DatePicker from "react-datepicker";
import { setHours, setMinutes } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import "../style/Consult.css";
import { Helmet } from "react-helmet";

const SERVICES = [
  { value: "Website",                    label: "Website" },
  { value: "Website + Booking",          label: "Website + Booking" },
  { value: "E-commerce",                 label: "E-Commerce" },
  { value: "Mobile App Development",     label: "Mobile App" },
  { value: "SaaS Development",           label: "SaaS Product" },
  { value: "AI Integration & Automation",label: "AI & Automation" },
  { value: "Custom System / Dashboard",  label: "Custom System" },
  { value: "SEO & Speed Optimization",   label: "SEO & Speed" },
  { value: "Website Maintenance",        label: "Maintenance" },
  { value: "Social Media & Ads",         label: "Social Media & Ads" },
  { value: "Not sure yet",               label: "Not sure yet" },
];

const BUSINESS_TYPES = [
  "Company / Startup",
  "Online Store",
  "Personal Brand",
  "Freelancer",
  "Business Center (Fitness, Spa, Studio, Education…)",
  "Medical Clinic",
  "Other",
];

const HOW_HEARD = [
  "Instagram / Facebook / TikTok",
  "Google search",
  "Friend or family recommendation",
  "Previous client",
  "Other",
];

const STEPS = ["Details", "Service", "Schedule", "Confirm"];

export default function Consultation() {
  const [step, setStep] = useState(0); // 0-3
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    businessType: "",
    businessName: "",
    service: "",
    howHeard: "",
    notes: "",
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  /* per-step validation */
  const canNext = () => {
    if (step === 0) return form.firstName && form.lastName && form.email && form.businessType;
    if (step === 1) return !!form.service;
    if (step === 2) return !!selectedDate;
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedDate) return;

    const dateStr = selectedDate.toLocaleDateString("en-US", {
      weekday: "long", month: "short", day: "numeric", year: "numeric",
    });
    const timeStr = selectedDate.toLocaleTimeString("en-US", {
      hour: "2-digit", minute: "2-digit",
    });

    const message =
`Hello Codix Team,

I would like to schedule a free consultation. Here are my details:

• Full Name: ${form.firstName} ${form.lastName}
• Email: ${form.email}
• Business Type: ${form.businessType}
• Business Name: ${form.businessName || "Not specified"}
• Service Interested In: ${form.service}
• Preferred Date & Time: ${dateStr} at ${timeStr}
• How I heard about you: ${form.howHeard || "Not specified"}
• Additional Notes: ${form.notes || "None"}

Thank you — I look forward to speaking with you.

Best regards,
${form.firstName} ${form.lastName}`;

    window.open(
      `https://wa.me/96181233235?text=${encodeURIComponent(message)}`,
      "_blank"
    );
    setSubmitted(true);
  };

  /* ── Success screen ── */
  if (submitted) {
    return (
      <div className="consult-page">
        <div className="consult-success">
          <div className="success-icon">✓</div>
          <h2>Request Sent!</h2>
          <p>
            We received your consultation request via WhatsApp. We'll confirm
            your slot within 24 hours.
          </p>
          <a href="https://codixlb.com" className="success-back">
            ← Back to Codix Tech
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Book a Free Consultation | Codix Tech</title>
        <meta
          name="description"
          content="Schedule your free consultation with Codix Tech — Lebanon's full-service digital agency. Web development, mobile apps, SaaS, AI, SEO and more."
        />
        <meta
          name="keywords"
          content="Codix Tech, Free Consultation, Web Development Lebanon, Book Consultation"
        />
        <meta name="author" content="Codix Tech" />
      </Helmet>

      <div className="consult-page">
        {/* Left panel */}
        <div className="consult-left">
          {/* Logo */}
          <a href="https://codixlb.com" className="consult-logo" aria-label="Codix Tech">
            <span className="logo-bracket">&lt;</span>
            <span className="logo-c">C</span>
            <span className="logo-odix">odix</span>
            <span className="logo-slash">/</span>
            <span className="logo-bracket">&gt;</span>
          </a>

          <div className="consult-left-content">
            <p className="consult-eyebrow">Free Consultation</p>
            <h1 className="consult-hero-title">
              Let's build<br />
              <span className="consult-orange">something great</span><br />
              together.
            </h1>
            <p className="consult-hero-sub">
              15–25 minute call with our team. No commitment, no pressure —
              just a focused conversation about your project and how we can help.
            </p>

            <ul className="consult-promises">
              {[
                "100% free — no hidden fees",
                "Mon – Sat, 9 AM to 8 PM",
                "Tailored advice for your business",
                "Quick WhatsApp confirmation",
              ].map((p) => (
                <li key={p}>
                  <span className="promise-check">✔</span>
                  {p}
                </li>
              ))}
            </ul>

            <div className="consult-contact-hint">
              <span>Or reach us directly:</span>
              <a href="https://wa.me/96181233235" target="_blank" rel="noreferrer">
                WhatsApp →
              </a>
              <a href="mailto:info@codixlb.com">
                info@codixlb.com →
              </a>
            </div>
          </div>
        </div>

        {/* Right panel — form */}
        <div className="consult-right">
          {/* Step progress */}
          <div className="consult-stepper" role="list" aria-label="Form steps">
            {STEPS.map((label, i) => (
              <div
                key={label}
                role="listitem"
                className={`
                  stepper-item
                  ${i === step ? "stepper-item--active" : ""}
                  ${i < step ? "stepper-item--done" : ""}
                `}
              >
                <div className="stepper-circle">
                  {i < step ? "✓" : String(i + 1)}
                </div>
                <span className="stepper-label">{label}</span>
                {i < STEPS.length - 1 && (
                  <div className={`stepper-line ${i < step ? "stepper-line--done" : ""}`} />
                )}
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="consult-form" noValidate>

            {/* ── Step 0: Details ── */}
            {step === 0 && (
              <div className="form-step">
                <div className="step-title">
                  <span className="step-mono">01.</span> Your Details
                </div>

                <div className="input-row">
                  <div className="field">
                    <label htmlFor="firstName">First Name</label>
                    <input
                      id="firstName"
                      type="text"
                      name="firstName"
                      placeholder="First Name"
                      required
                      value={form.firstName}
                      onChange={handleChange}
                      autoComplete="given-name"
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="lastName">Last Name</label>
                    <input
                      id="lastName"
                      type="text"
                      name="lastName"
                      placeholder="Last Name"
                      required
                      value={form.lastName}
                      onChange={handleChange}
                      autoComplete="family-name"
                    />
                  </div>
                </div>

                <div className="field">
                  <label htmlFor="email">Email Address</label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    required
                    value={form.email}
                    onChange={handleChange}
                    autoComplete="email"
                  />
                </div>

                <div className="field">
                  <label htmlFor="businessType">Business Type</label>
                  <select
                    id="businessType"
                    name="businessType"
                    required
                    value={form.businessType}
                    onChange={handleChange}
                  >
                    <option value="">Select your business type…</option>
                    {BUSINESS_TYPES.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label htmlFor="businessName">
                    Business Name <span className="optional">(optional)</span>
                  </label>
                  <input
                    id="businessName"
                    type="text"
                    name="businessName"
                    placeholder="Your company or project name"
                    value={form.businessName}
                    onChange={handleChange}
                  />
                </div>
              </div>
            )}

            {/* ── Step 1: Service ── */}
            {step === 1 && (
              <div className="form-step">
                <div className="step-title">
                  <span className="step-mono">02.</span> Service Needed
                </div>
                <p className="step-hint">Select the service that best fits your project.</p>
                <div className="service-grid">
                  {SERVICES.map(({ value, label }) => (
                    <label
                      key={value}
                      className={`svc-pill ${form.service === value ? "svc-pill--active" : ""}`}
                    >
                      <input
                        type="radio"
                        name="service"
                        value={value}
                        checked={form.service === value}
                        onChange={handleChange}
                      />
                      {label}
                    </label>
                  ))}
                </div>

                <div className="field" style={{ marginTop: "24px" }}>
                  <label htmlFor="howHeard">How did you hear about us?</label>
                  <select
                    id="howHeard"
                    name="howHeard"
                    value={form.howHeard}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select an option…</option>
                    {HOW_HEARD.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* ── Step 2: Schedule ── */}
            {step === 2 && (
              <div className="form-step">
                <div className="step-title">
                  <span className="step-mono">03.</span> Pick a Date & Time
                </div>
                <p className="step-hint">
                  Available Mon – Sat, 9:00 AM – 8:00 PM. Sundays excluded.
                </p>

                <div className="datepicker-wrap">
                  <DatePicker
                    selected={selectedDate}
                    onChange={(date) => setSelectedDate(date)}
                    showTimeSelect
                    timeIntervals={30}
                    minDate={new Date()}
                    filterDate={(date) => date.getDay() !== 0}
                    minTime={
                      selectedDate &&
                      selectedDate.toDateString() === new Date().toDateString()
                        ? new Date()
                        : setHours(setMinutes(new Date(), 0), 9)
                    }
                    maxTime={setHours(setMinutes(new Date(), 0), 20)}
                    dateFormat="EEEE, MMM d, yyyy — h:mm aa"
                    placeholderText="Click to choose your slot…"
                    inline
                  />
                </div>

                {selectedDate && (
                  <div className="selected-slot">
                    <span className="slot-check">✔</span>
                    <div>
                      <span className="slot-label">Selected slot</span>
                      <span className="slot-value">
                        {selectedDate.toLocaleDateString("en-US", {
                          weekday: "long", month: "short", day: "numeric",
                        })}{" "}
                        at{" "}
                        {selectedDate.toLocaleTimeString("en-US", {
                          hour: "2-digit", minute: "2-digit",
                        })}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Step 3: Confirm ── */}
            {step === 3 && (
              <div className="form-step">
                <div className="step-title">
                  <span className="step-mono">04.</span> Confirm & Send
                </div>
                <p className="step-hint">
                  Review your details. We'll open WhatsApp to confirm your booking.
                </p>

                {/* Summary card */}
                <div className="summary-card">
                  {[
                    { label: "Name",     value: `${form.firstName} ${form.lastName}` },
                    { label: "Email",    value: form.email },
                    { label: "Business", value: `${form.businessType}${form.businessName ? ` — ${form.businessName}` : ""}` },
                    { label: "Service",  value: form.service },
                    { label: "Slot",     value: selectedDate
                        ? `${selectedDate.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })} at ${selectedDate.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}`
                        : "—"
                    },
                    { label: "Source",   value: form.howHeard || "—" },
                  ].map(({ label, value }) => (
                    <div key={label} className="summary-row">
                      <span className="summary-label">{label}</span>
                      <span className="summary-value">{value}</span>
                    </div>
                  ))}
                </div>

                <div className="field">
                  <label htmlFor="notes">
                    Additional notes <span className="optional">(optional)</span>
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    placeholder="Anything else you'd like us to know before the call…"
                    rows={3}
                    value={form.notes}
                    onChange={handleChange}
                  />
                </div>

                <p className="confirm-note">
                  Clicking the button below will open WhatsApp with a pre-filled
                  message. Just hit send — we'll confirm your slot shortly.
                </p>
              </div>
            )}

            {/* ── Navigation ── */}
            <div className="form-nav">
              {step > 0 && (
                <button
                  type="button"
                  className="nav-btn nav-btn--back"
                  onClick={() => setStep((s) => s - 1)}
                >
                  ← Back
                </button>
              )}

              {step < STEPS.length - 1 ? (
                <button
                  type="button"
                  className={`nav-btn nav-btn--next ${!canNext() ? "nav-btn--disabled" : ""}`}
                  onClick={() => { if (canNext()) setStep((s) => s + 1); }}
                  disabled={!canNext()}
                >
                  Continue →
                </button>
              ) : (
                <button type="submit" className="nav-btn nav-btn--submit">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347"/>
                  </svg>
                  Send via WhatsApp
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
